import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Field,
	FieldType,
	Label,
	Modal,
	Paragraph,
	Popover,
	Spinner,
} from '@treams/treams-ui';

import EvaluationSubmissionRequired from './EvaluationSubmissionRequired';
import { CompetencyLevelsInput } from './components/input/CompetencyInput';

export default function EvaluationSubmissionScoreModal(props: {
	submission: graphql.EvaluationSubmission;
	competencyLevelSet: graphql.CompetencyLevelSet;
	saving: boolean;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();

	const score = props.submission.overrideScore;

	const [submitted, setSubmitted] = useState(false);

	const formik = useFormik({
		initialValues: {
			competency: {
				competency: score
					? (props.competencyLevelSet.levels.findIndex((level) => level.id === score.id) ??
						undefined)
					: undefined,
			},
			reason: props.submission.overrideReason ?? '',
		},
		onSubmit: () => {},
	});
	const [commit, { loading }] = graphql.useOverrideEvaluationSubmissionScoreMutation({});

	const onConfirm = async () => {
		setSubmitted(true);

		if (formik.values.competency.competency === undefined || formik.values.reason.length === 0) {
			return;
		}

		await commit({
			variables: {
				input: {
					id: props.submission.id,
					competencyLevelID:
						props.competencyLevelSet.levels[formik.values.competency.competency!]?.id,
					reason: formik.values.reason || undefined,
				},
			},
		});

		props.onDismiss();
	};

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header>{t('evaluation.submission.overrideScore.modal.title')}ss</Modal.Header>
				<Modal.Contents>
					<Paragraph>{t('evaluation.submission.overrideScore.modal.description')}</Paragraph>
					<Box mt={3}>
						<FormikProvider value={formik}>
							<Box mt={3}>
								<Label mb={2}>{t('evaluation.submission.overrideScore.modal.reason.label')} </Label>
								<Field
									name="reason"
									type={FieldType.Text}
									fullWidth
									placeholder={t('evaluation.submission.overrideScore.modal.reason.placeholder')}
								/>
								{submitted && formik.values.reason.length === 0 && (
									<Box mb={3}>
										<EvaluationSubmissionRequired>
											{t('evaluation.submission.overrideScore.modal.required')}
										</EvaluationSubmissionRequired>
									</Box>
								)}
							</Box>
							<CompetencyLevelsInput
								levels={props.competencyLevelSet.levels.map((level) => ({
									enabled: true,
									level,
								}))}
								name="competency"
								party={graphql.EvaluationSubmissionParty.Manager}
							/>
							{submitted && formik.values.competency.competency === undefined && (
								<Box mt={3}>
									<EvaluationSubmissionRequired>
										{t('evaluation.submission.overrideScore.score.required')}
									</EvaluationSubmissionRequired>
								</Box>
							)}
						</FormikProvider>
					</Box>
				</Modal.Contents>
				<Modal.Footer>
					{props.saving || loading ? (
						<Spinner centered />
					) : (
						<Flex px={3} alignItems="center" justifyContent="flex-end">
							<Box mr={2}>
								<Button
									type="secondary"
									title={t('evaluation.submission.overrideScore.modal.cancel')}
									onClick={() => props.onDismiss()}
								/>
							</Box>
							<Button
								title={t('evaluation.submission.overrideScore.modal.confirm')}
								onClick={onConfirm}
							/>
						</Flex>
					)}
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
